import { Component, Inject, Injector, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { IDto } from '@interfaces/dto';
import { IEntity } from '@interfaces/entity';
import { DtoService } from '@services/dto/dto.service';
import { EntityService } from '@services/entity/entity.service';
import { FormService } from '@services/form/form.service';
import { EventService } from '@services/event/event.service';
import { RouteDataService } from '@services/route-data/route-data.service';
import { Subscription } from 'rxjs';
import { TableFunctionsService } from '@services/table/table-functions.service';
import { SelectEntitiesService } from '@services/entity/select-entities.service';
import { Table } from '@models/table';
import { TableColumn } from '@models/tableColumn';
import { TableOptions } from '@models/tableOptions';
import { ComponentBase } from '@abstracts/component-base';

@Component({
  template: '',
  providers: [TableFunctionsService]
})
export abstract class AbstractEntitiesTableComponent extends ComponentBase implements OnInit, OnDestroy {

  public table: Table;
  public tableColumns: TableColumn[];
  public tableOptions: TableOptions;
  public entities: IEntity[] | IDto[];

  public routerSubscription: Subscription;
  public tableReady: boolean = false;
  public noPagination: boolean;


  @Inject(TableFunctionsService)
  public tableFunctions: TableFunctionsService;

  @Inject(Router)
  public router: Router;

  @Inject(EventService)
  public eventService: EventService;

  @Inject(ActivatedRoute)
  public activatedRoute: ActivatedRoute;


  @ViewChild('actionsTemplate') actionsTemplate: TemplateRef<any>;

  constructor(public entityService: EntityService, injector: Injector) {
    super(injector);
    this.tableFunctions = injector.get(TableFunctionsService);
    this.router = injector.get(Router);
    this.eventService = injector.get(EventService);
    this.activatedRoute = injector.get(ActivatedRoute);
  }

  ngOnInit(): void {
    super.ngOnInit();

    this.initializeEntities();

    this.routerSubscription = this.router.events.subscribe(
      (event) => {
        if (event instanceof NavigationEnd) {
          this.initializeEntities();
        }
      });

    this.eventService.entityRefresh$.subscribe(result => {
      this.initializeEntities();
    });

    this.tableFunctions.sortColumn = '';
    this.tableFunctions.searchTerm = '';
    this.tableFunctions.searchTermMap = new Map<string, string>();
    this.tableFunctions.page = 1;
    this.tableFunctions.pageSize = this.getPageSizeFromLocalStorage();
    this.tableFunctions.selectedEntities = [];
  }

  getPageSizeFromLocalStorage(): number {
    const lastSegment = this.getLastSegmentIfCustomer(this.router.url);
    let size: number | null = null;

    if (lastSegment != null) {
      const storedSize = localStorage.getItem(`Operator${lastSegment}PageSize`);
      size = storedSize ? parseInt(storedSize, 10) : null;
    } else {
      const storedSize = localStorage.getItem(`${this.className}PageSize`);
      size = storedSize ? parseInt(storedSize, 10) : null;
    }

    return size || 10;
  }

  getLastSegmentIfCustomer(url: string): string | null {
    if (url.includes('customer')) {
      const segments = url.split('/');
      return segments.pop() || '';
    }
    return null;
  }

  ngOnDestroy(): void {
    if (this.routerSubscription) this.routerSubscription.unsubscribe();
  }

  setupTable() {
    this.table = {
      entityService: this.entityService,
      tableData: this.entities,
      tableFunctions: this.tableFunctions,
      tableColumns: this.tableColumns,
      tableOptions: this.tableOptions,
      actionsTemplate: this.actionsTemplate,
      matchFunction: this.matches
    }

    if (this.entities?.length == 0) {
      this.noPagination = true;
    } else {
      this.noPagination = false;
    }

    if (this.entities.length > 10) {
      this.componentSettings.footerVisible = true;
    }
    else {
      this.componentSettings.footerVisible = false;
    }

    this.tableReady = true;
  }

  abstract matches(entity, term);
  abstract initializeEntities();
}
